import { useMemo, useState } from "react";
import { Controller, Control } from "react-hook-form";
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from "@headlessui/react";
import { ChevronDown, Check, Search, Plus } from "lucide-react";

interface ScreenComboboxProps {
  value: string;
  onChange: (val: string) => void;
  screens: string[];
  error?: string;
  label?: string;
  required?: boolean;
  placeholder?: string;
}

// Separate component so autoFocus fires every time the panel mounts
function SearchInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="px-3 py-2 border-b border-gray-100 bg-white">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
        <input
          autoFocus
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => e.stopPropagation()}
          onKeyUp={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          placeholder="Search…"
          className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-brand-400"
        />
      </div>
    </div>
  );
}

export function ScreenCombobox({
  value,
  onChange,
  screens,
  error,
  label,
  required,
  placeholder = "Select screen name",
}: ScreenComboboxProps) {
  const [search, setSearch] = useState("");

  const normalizedScreens = screens.map((s) =>
    typeof s === "string" ? s : (s as any).Screen_Name ?? (s as any).name ?? String(s)
  );

  const filtered = useMemo(() => {
    if (!search.trim()) return normalizedScreens;

    return normalizedScreens.filter((s) =>
      s.toLowerCase().includes(search.toLowerCase())
    );
  }, [search, normalizedScreens]);

  const exactMatch = normalizedScreens.some(
    (s) => s.toLowerCase() === search.toLowerCase().trim()
  );
  const showManual = !!search.trim() && !exactMatch;

  const handleChange = (val: string) => {
    onChange(val);
    setSearch("");
  };

  return (
    <div>
      {label && (
        <label className="block text-md font-bold text-black mb-1">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      <Listbox value={value} onChange={handleChange}>
        <ListboxButton
          className={`w-full flex items-center justify-between px-3 py-3 bg-white border rounded-lg text-sm text-left focus:outline-none data-[focus]:ring-2 data-[focus]:ring-brand-400 transition-colors ${error ? "border-red-500" : "border-gray-300 hover:border-gray-400"
            }`}
        >
          <span className={`truncate ${value ? "text-gray-900" : "text-gray-400"}`}>
            {value || placeholder}
          </span>
          <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" />
        </ListboxButton>

        <ListboxOptions
          anchor="bottom start"
          className="z-[99999] w-[var(--button-width)] bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden focus:outline-none [--anchor-gap:4px]"
        >
          {/* Search input — autoFocus fires each time this panel mounts */}
          <SearchInput value={search} onChange={setSearch} />

          {/* Options list */}
          <div className="max-h-44 overflow-y-auto py-1">
            {filtered.length === 0 && !showManual ? (
              <div className="px-3 py-2.5 text-sm text-gray-400 italic text-center select-none">
                No options found
              </div>
            ) : (
              filtered.map((s, index) => (
                <ListboxOption
                  key={`${s}-${index}`}
                  value={s}
                  className="flex items-center justify-between px-3 py-2.5 text-sm cursor-pointer select-none hover:bg-brand-50 hover:text-brand-700 text-gray-800 transition-colors data-[selected]:text-brand-500 data-[selected]:font-medium"
                >
                  <span className="truncate text-left">{s}</span>
                  <Check className="w-3.5 h-3.5 text-brand-500 flex-shrink-0 ml-2 hidden data-[selected]:block" />
                </ListboxOption>
              ))
            )}

            {showManual && (
              <ListboxOption
                value={search.trim()}
                className="flex items-center gap-2 px-3 py-2.5 text-sm cursor-pointer select-none text-brand-500 hover:bg-brand-50 border-t border-gray-100 font-medium transition-colors"
              >
                <Plus className="w-3.5 h-3.5 flex-shrink-0" />
                Use &ldquo;<span className="font-semibold">{search.trim()}</span>&rdquo; as screen name
              </ListboxOption>
            )}
          </div>
        </ListboxOptions>
      </Listbox>

      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
}

// Controller-wrapped version for react-hook-form
interface ScreenComboboxFieldProps {
  control: Control<any>;
  name: string;
  screens: string[];
  error?: string;
  label?: string;
  required?: boolean;
  placeholder?: string;
}

export function ScreenComboboxField({
  control,
  name,
  screens,
  error,
  label,
  required,
  placeholder,
}: ScreenComboboxFieldProps) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => (
        <ScreenCombobox
          value={field.value ?? ""}
          onChange={field.onChange}
          screens={screens}
          error={fieldState.error?.message ?? error}
          label={label}
          required={required}
          placeholder={placeholder}
        />
      )}
    />
  );
}
